import React, { useEffect, useState } from 'react';
import {
	Box,
	Button,
	Grid,
	IconButton,
	Menu,
	MenuItem,
	OutlinedInput,
	Popover,
	Select,
	SvgIconProps,
	Tooltip,
	Typography
} from '@mui/material';
import moment from 'moment';
import { DateRangeIcon } from 'components/CommonIcons/Icons';
import { useTheme } from '@mui/material/styles';

export type DataEstateDateFilterProps = {
	filterMonth: string;
	customMonth: string;
	setFilterMonth: React.Dispatch<React.SetStateAction<string>>;
	setCustomMonth: React.Dispatch<React.SetStateAction<string>>;
	handleChange: (selectedDate: string, isCustomDate?: boolean) => void;
	minYear?: number;
	disableFutureMonths?: boolean;
};

const STANDARD_OPTIONS: { value: string; label: string }[] = [
	{ value: '0M', label: 'This Month' },
	{ value: '1M', label: 'Last Month' },
	{ value: '3M', label: 'Last 3 Months' },
	{ value: '6M', label: 'Last 6 Months' },
	{ value: '9M', label: 'Last 9 Months' },
	{ value: '0Y', label: 'This Year' },
	{ value: '1Y', label: 'Last Year' },
	{ value: '2Y', label: 'Last 2 Years' },
];

const monthMap: { [key: string]: string } = {
	Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
	Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12'
};

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const NAVY_BLUE = '#003366';
const CYAN_BLUE = '#1da1d2';

const DataEstateDateFilter: React.FC<DataEstateDateFilterProps> = ({
	filterMonth,
	customMonth,
	setFilterMonth,
	setCustomMonth,
	handleChange,
	minYear = 2018,
	disableFutureMonths = true,
}) => {
	const theme = useTheme();
	const colors = theme.palette;

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [customPickerAnchorEl, setCustomPickerAnchorEl] = useState<null | HTMLElement>(null);

	const [selectedYear, setSelectedYear] = useState<number>(() => {
		if (customMonth && /^\d{4}/.test(customMonth)) {
			return Number(customMonth.substring(0, 4));
		}
		return new Date().getFullYear();
	});
	const [displayMonth, setDisplayMonth] = useState<string | null>(() => {
		if (customMonth && /^\d{4}-\d{2}/.test(customMonth)) {
			const mVal = customMonth.substring(5, 7);
			return Object.keys(monthMap).find((k) => monthMap[k] === mVal) || null;
		}
		return null;
	});
	const [isAllMonths, setIsAllMonths] = useState<boolean>(() => {
		return Boolean(customMonth && /^\d{4}$/.test(customMonth));
	});

	const isMenuOpen = Boolean(anchorEl);
	const isCustomPickerOpen = Boolean(customPickerAnchorEl);

	useEffect(() => {
		if (customMonth && /^\d{4}-\d{2}/.test(customMonth)) {
			setSelectedYear(Number(customMonth.substring(0, 4)));
			const mVal = customMonth.substring(5, 7);
			setDisplayMonth(Object.keys(monthMap).find((k) => monthMap[k] === mVal) || null);
			setIsAllMonths(false);
		} else if (customMonth && /^\d{4}$/.test(customMonth)) {
			setSelectedYear(Number(customMonth));
			setDisplayMonth(null);
			setIsAllMonths(true);
		}
	}, [customMonth]);

	const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
	};

	const handleSelectOption = (selectedValue: string) => {
		if (selectedValue === 'customMonth') {
			setCustomPickerAnchorEl(anchorEl);
			handleCloseMenu();
			return;
		}

		setCustomMonth('');
		setFilterMonth(selectedValue);
		handleChange(selectedValue);
		handleCloseMenu();
	};

	const handleCloseCustomPicker = () => {
		setCustomPickerAnchorEl(null);
	};

	const handleYearChange = (newYear: number) => {
		setSelectedYear(newYear);
		setIsAllMonths(false);
		setDisplayMonth(null);
	};

	const handleMonthClick = (monthName: string) => {
		if (disableFutureMonths) {
			const monthIndex = months.indexOf(monthName);
			const now = new Date();
			const currentYear = now.getFullYear();
			const currentMonth = now.getMonth();

			if (selectedYear === currentYear && monthIndex > currentMonth) {
				return;
			}
		}

		const formattedDate = `${selectedYear}-${monthMap[monthName]}`;
		const labelDate = moment(formattedDate, 'YYYY-MM').format('MMMM YYYY');

		setDisplayMonth(monthName);
		setIsAllMonths(false);
		setFilterMonth(labelDate);
		setCustomMonth(formattedDate);
		handleChange(formattedDate, true);
		handleCloseCustomPicker();
	};

	const handleAllMonthsClick = () => {
		const formattedYear = `${selectedYear}`;
		setIsAllMonths(true);
		setDisplayMonth(null);
		setFilterMonth(`${formattedYear}, All Months`);
		setCustomMonth(formattedYear);
		handleChange(formattedYear, true);
		handleCloseCustomPicker();
	};

	const getDisplayLabel = () => {
		if (customMonth) {
			if (/^\d{4}$/.test(customMonth)) {
				return `${customMonth}, All Months`;
			}
			return moment(customMonth, 'YYYY-MM').format('MMMM YYYY');
		}
		const found = STANDARD_OPTIONS.find((opt) => opt.value === filterMonth);
		if (found) return found.label;
		if (filterMonth && filterMonth !== '0M') return filterMonth;
		return 'This Month';
	};

	const dropdownBg = (colors as any)?.dropdown?.menuItemBackground || '#0A2744';
	const dropdownText = (colors as any)?.dropdown?.menuItemText || '#ffffff';
	const dropdownSelected = (colors as any)?.dropdown?.menuItemSelected || '#153D69';
	const dropdownHover = (colors as any)?.dropdown?.menuItemHover || '#003771';
	const dropdownHoverText = (colors as any)?.dropdown?.menuItemHoverText || '#ffffff';

	const menuItemSx = {
		...(theme.typography as any).filterText,
		padding: '6px 16px',
		backgroundColor: `${dropdownBg} !important`,
		color: `${dropdownText} !important`,
		fontSize: '14px',
		'&.Mui-selected': {
			backgroundColor: `${dropdownSelected} !important`,
			color: `${dropdownHoverText} !important`,
		},
		'&&:hover, &&.MuiMenuItem-root:hover, &.Mui-selected:hover': {
			backgroundColor: `${dropdownHover} !important`,
			color: `${dropdownHoverText} !important`,
		},
	};

	return (
		<>
			<Tooltip title="Select Date Range" placement="bottom">
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 0.5,
						cursor: 'pointer',
						userSelect: 'none',
					}}
					onClick={handleOpenMenu}
				>
					<Typography
						sx={{
							color: '#48B5ED',
							fontSize: '14px',
							fontWeight: 500,
							fontFamily: 'Montserrat',
							whiteSpace: 'nowrap',
						}}
					>
						{getDisplayLabel()}
					</Typography>
					<IconButton size="small" aria-label="Open Calendar" sx={{ p: 0 }}>
						<DateRangeIcon />
					</IconButton>
				</Box>
			</Tooltip>

			<Menu
				anchorEl={anchorEl}
				open={isMenuOpen}
				onClose={handleCloseMenu}
				sx={{
					'&& .MuiPaper-root .MuiMenuItem-root:hover': {
						backgroundColor: `${dropdownHover} !important`,
						color: `${dropdownHoverText} !important`,
					},
					'&& .MuiPaper-root .MuiMenuItem-root.Mui-selected:hover': {
						backgroundColor: `${dropdownHover} !important`,
						color: `${dropdownHoverText} !important`,
					},
				}}
				PaperProps={{
					sx: {
						backgroundColor: `${dropdownBg} !important`,
						borderRadius: '4px',
						boxShadow: '0px 4px 20px rgba(0,0,0,0.15)',
						minWidth: 160,
						mt: 0.5,
						'&& .MuiMenuItem-root:hover': {
							backgroundColor: `${dropdownHover} !important`,
							color: `${dropdownHoverText} !important`,
						},
						'&& .MuiMenuItem-root.Mui-selected:hover': {
							backgroundColor: `${dropdownHover} !important`,
							color: `${dropdownHoverText} !important`,
						},
					},
				}}
				MenuListProps={{
					sx: {
						paddingTop: 0,
						paddingBottom: 0,
						'&& .MuiMenuItem-root:hover': {
							backgroundColor: `${dropdownHover} !important`,
							color: `${dropdownHoverText} !important`,
						},
						'&& .MuiMenuItem-root.Mui-selected:hover': {
							backgroundColor: `${dropdownHover} !important`,
							color: `${dropdownHoverText} !important`,
						},
					},
				}}
			>
				{STANDARD_OPTIONS.map((opt) => (
					<MenuItem
						key={opt.value}
						selected={!customMonth && filterMonth === opt.value}
						onClick={() => handleSelectOption(opt.value)}
						sx={menuItemSx}
					>
						{opt.label}
					</MenuItem>
				))}
				<MenuItem
					selected={Boolean(customMonth)}
					onClick={() => handleSelectOption('customMonth')}
					sx={menuItemSx}
				>
					Custom Month
				</MenuItem>
			</Menu>

			<Popover
				open={isCustomPickerOpen}
				anchorEl={customPickerAnchorEl}
				onClose={handleCloseCustomPicker}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				slotProps={{
					paper: {
						sx: {
							bgcolor: 'white !important',
							borderRadius: '4px',
							border: `1px solid ${NAVY_BLUE}`,
							width: 350,
							p: 4,
							boxShadow: '0px 4px 20px rgba(0,0,0,0.15)',
						},
					},
				}}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
					<Select
						value={selectedYear}
						onChange={(e) => handleYearChange(Number(e.target.value))}
						size="small"
						input={<OutlinedInput sx={{ borderRadius: '4px' }} />}
						sx={{
							width: 100,
							bgcolor: CYAN_BLUE,
							borderRadius: 0,
							color: 'white',
							fontWeight: 600,
							'& .MuiSelect-select': { color: 'white', py: 0.5, px: 1.5 },
							'& .MuiSvgIcon-root': { color: 'white' },
							'& .MuiOutlinedInput-notchedOutline': { borderColor: CYAN_BLUE },
						}}
						MenuProps={{
							PaperProps: {
								sx: {
									bgcolor: '#ffffff !important',
									boxShadow: '0px 4px 20px rgba(0,0,0,0.15)',
									'& .MuiMenuItem-root': {
										color: '#000000 !important',
										'&.Mui-selected': {
											bgcolor: 'rgba(0, 0, 0, 0.08) !important',
											color: '#000000 !important',
										},
										'&:hover': {
											bgcolor: 'rgba(0, 0, 0, 0.04) !important',
											color: '#000000 !important',
										},
									},
								},
							},
						}}
					>
						{Array.from(
							{ length: new Date().getFullYear() - minYear + 1 },
							(_, index) => minYear + index
						).map((year) => (
							<MenuItem key={year} value={year} sx={{ color: '#000000 !important' }}>
								{year}
							</MenuItem>
						))}
					</Select>

					<Button
						onClick={handleAllMonthsClick}
						disabled={isAllMonths}
						sx={{
							textTransform: 'none',
							fontWeight: 600,
							color: isAllMonths ? '#ccc' : CYAN_BLUE,
							'&:hover': { bgcolor: 'transparent', opacity: 0.8 },
						}}
					>
						All Months
					</Button>
				</Box>

				<Grid container sx={{ border: '1px solid #eef2f6' }}>
					{months.map((month, index) => {
						const isActive = !isAllMonths && displayMonth === month;
						const now = new Date();
						const currentYear = now.getFullYear();
						const currentMonth = now.getMonth();
						const isDisabled = disableFutureMonths && selectedYear === currentYear && index > currentMonth;
						const getTextColor = () => {
							if (isDisabled) return '#ccc';
							if (isActive) return 'white';
							return '#666';
						};
						const getHoverBgColor = () => {
							if (isDisabled) return 'transparent';
							if (isActive) return CYAN_BLUE;
							return '#f5f5f5';
						};

						return (
							<Grid item xs={4} key={month} sx={{ border: '0.5px solid #eef2f6' }}>
								<Box
									onClick={() => !isDisabled && handleMonthClick(month)}
									sx={{
										height: 55,
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										cursor: isDisabled ? 'not-allowed' : 'pointer',
										bgcolor: isActive ? CYAN_BLUE : 'transparent',
										color: getTextColor(),
										opacity: isDisabled ? 0.5 : 1,
										fontFamily: 'Montserrat, sans-serif',
										fontSize: '13px',
										fontWeight: isActive ? 600 : 400,
										'&:hover': { bgcolor: getHoverBgColor() },
									}}
								>
									{month}
								</Box>
							</Grid>
						);
					})}
				</Grid>
			</Popover>
		</>
	);
};

export default DataEstateDateFilter; 